import React, { createContext, useEffect, useReducer } from 'react';
import jwtDecode from 'jwt-decode';
import axios from 'axios.js';
import { MatxLoading } from 'app/components';
import { loginUrl } from 'app/helper/ApiUrlHelper';

const initialState = {
  isAuthenticated: false,
  isInitialised: false,
  user: null,
};

const isValidToken = (accessToken) => {
  if (!accessToken) {
    return false;
  }

  const decodedToken = jwtDecode(accessToken);
  const currentTime = Date.now() / 1000;

  return decodedToken.exp > currentTime;
};

const setSession = (accessToken, roleId, userId) => {
  if (accessToken) {
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('roleId', roleId);
    localStorage.setItem('userId', userId);
    axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
  } else {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('roleId');
    localStorage.removeItem('userId');
    delete axios.defaults.headers.common.Authorization;
  }
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'INIT': {
      const { isAuthenticated } = action.payload;

      return {
        ...state,
        isAuthenticated,
        isInitialised: true,
      };
    }
    case 'LOGIN': {
      return {
        ...state,
        isAuthenticated: true,
      };
    }
    case 'LOGOUT': {
      return {
        ...state,
        isAuthenticated: false,
      };
    }
    case 'REGISTER': {
      return {
        ...state,
        isAuthenticated: true,
      };
    }
    default: {
      return { ...state };
    }
  }
};

const AuthContext = createContext({
  ...initialState,
  method: 'JWT',
  login: () => Promise.resolve(),
  logout: () => { },
});

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const login = async (email, password) => {
    const response = await axios.post(loginUrl(), {
      email,
      password,
    });


    const { errorCode, data, message } = response.data;

    if (errorCode === 0 && data.requiresOtp) {
      // OTP is required → store temp session data
      localStorage.setItem('otpUserId', data.otpSessionId);
      localStorage.setItem('roleId', data.role_id); // optional for UI purposes

      // redirect to OTP page
      window.location.href = '/session/otp-verification';
      return;
    }

    if (errorCode === 0 && data.token) {
      const accessToken = data.token;
      const roleId = data.role_id;
      const userId = data.user_id;
      setSession(accessToken, roleId, userId);

      dispatch({
        type: 'LOGIN',
        payload: {
          roleId,
        },
      });
    } else {
      alert(message);
    }
  };

  const logout = () => {
    setSession(null);
    localStorage.removeItem('accessToken');
    localStorage.removeItem('roleId');
    localStorage.removeItem('userId');
    dispatch({ type: 'LOGOUT' });
  };

  useEffect(() => {
    const enforceOtpWindowRules = () => {
      const now = new Date();
      const currentDay = now.getDay(); // 0 = Sunday
      const currentHour = now.getHours();
      const currentMinutes = now.getMinutes();

      const otpVerified = localStorage.getItem('otp_verified');

      const isSunday = currentDay === 0;

      const isEveningToMorningWindow =
        (currentHour > 19 || (currentHour === 19 && currentMinutes >= 30)) || // After 7:30 PM
        (currentHour < 9); // Before 9:00 AM

      if ((isSunday || isEveningToMorningWindow) && !otpVerified) {
        console.warn('Forcing logout due to missing OTP verification');

        // Clear everything and dispatch logout
        setSession(null);
        localStorage.removeItem('accessToken');
        localStorage.removeItem('roleId');
        localStorage.removeItem('otp_verified');
        localStorage.removeItem('otpUserId');

        dispatch({ type: 'LOGOUT' });
      }
    };

    (async () => {
      try {
        const accessToken = localStorage.getItem('accessToken');
        const roleId = localStorage.getItem('roleId');
        const userId = localStorage.getItem('userId');

        if (accessToken && isValidToken(accessToken)) {
          setSession(accessToken, roleId, userId);

          dispatch({
            type: 'INIT',
            payload: { isAuthenticated: true },
          });

          // ✅ Check OTP enforcement
          enforceOtpWindowRules();
        } else {
          dispatch({
            type: 'INIT',
            payload: {
              isAuthenticated: false,
              user: null,
            },
          });
        }
      } catch (err) {
        console.error(err);
        dispatch({
          type: 'INIT',
          payload: {
            isAuthenticated: false,
          },
        });
      }
    })();
  }, []);

  if (!state.isInitialised) {
    return <MatxLoading />;
  }

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'JWT',
        login,
        logout,
        dispatch,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
